// Callout — tone-driven info/warn/success/critical panel.
//
// Tone vocabulary mirrored from MM-PORTAL-DNA-MIRROR §6.4. Body is plain
// text (no markdown) — walkthroughs stay terse. The visual treatment is
// intentionally close to the admin portal's own toast/banner palette so
// operators feel they're reading MM, not a third-party doc.

import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'

import type { Callout as CalloutT } from './types'

const TONE_STYLE = {
  info: {
    icon: Info,
    className:
      'border-butler-500/40 bg-butler-500/10 text-butler-700 dark:border-butler-300/40 dark:bg-butler-500/10 dark:text-butler-100',
  },
  warn: {
    icon: AlertTriangle,
    className:
      'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  },
  success: {
    icon: CheckCircle2,
    className:
      'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100',
  },
  critical: {
    icon: XCircle,
    className:
      'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100',
  },
} as const

interface CalloutProps {
  callout: CalloutT
}

export function Callout({ callout }: CalloutProps) {
  const tone = TONE_STYLE[callout.tone] || TONE_STYLE.info
  const Icon = tone.icon
  return (
    <div
      role="note"
      data-testid={`help-callout-${callout.tone}`}
      className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm ${tone.className}`}
    >
      <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <p className="leading-snug">{callout.body}</p>
    </div>
  )
}
